import format from 'string-template';
import * as Sentry from '@sentry/browser';

import { pathPlaceholder, storagePaths } from '../../../../../utils/firebasePaths';
import { StorageProcessState } from '../../../../../hooks/useStorage';
import { FileType } from '../../../../../chat/utils/files';
import Coach from '../../../../../Model/Coach';

/** PDFs get their own row treatment, so they are typed apart from other files. */
const resolveFileType = (mimeType = '') => {
  if (mimeType.startsWith('application/pdf')) return FileType.PDF;
  if (mimeType.startsWith('image/')) return FileType.IMAGE;
  if (mimeType.startsWith('video/')) return FileType.VIDEO;
  if (mimeType.startsWith('audio/')) return FileType.AUDIO;
  return FileType.FILE;
};

/**
 * Uploads newly selected files to the coach's library-documents storage folder.
 *
 * Uploaded once regardless of how many products they are being published to: the
 * products each get their own record pointing at the same stored file, so adding a
 * document to three libraries does not store it three times.
 *
 * A partial failure deletes whatever did land, so a retry cannot leave orphaned
 * blobs behind.
 *
 * @param {object} params - Upload inputs.
 * @param {Array<object>} params.attachments - Staged files.
 * @param {string} params.coachId - The coach whose folder to upload into.
 * @param {Function} params.uploadAttachments - From useStorage.
 * @param {Function} params.deleteAttachments - From useStorage.
 * @returns {Promise<{uploaded: Array<object>, hasError: boolean}>} Uploaded files and failure state.
 */
export const uploadLibraryDocuments = async ({
  attachments,
  coachId,
  uploadAttachments,
  deleteAttachments,
}) => {
  if (!attachments.length) return { uploaded: [], hasError: false };

  const storagePath = format(storagePaths.COACH_LIBRARY_DOCUMENTS, {
    [pathPlaceholder.COACH_ID]: coachId,
  });

  try {
    /*
      `assetPath` is the identity of a publish — this page groups its rows by it,
      mobile dedupes by it, and the delete cleanup decides whether a stored file is
      still referenced by it. All three hold only because `uploadAttachment` appends
      a uuid to every name, which it does unless `useExactFileName` is passed.
      Passing it here would let two publishes of the same filename share a path,
      silently merge into one row, and have one delete take the other's file.
    */
    const result = await uploadAttachments(attachments, storagePath);

    const uploaded = [];
    let hasError = false;
    result.forEach((res, index) => {
      if (res.state === StorageProcessState.SUCCESS) {
        const mimeType = res.metadata?.contentType || attachments[index].fileType || '';
        uploaded.push({
          fileName: attachments[index].name,
          assetPath: res.fileRef,
          mimeType,
          type: resolveFileType(mimeType),
          fileSize: Number(res.metadata?.size ?? 0),
        });
      } else {
        hasError = true;
      }
    });

    if (hasError) {
      // Roll back the ones that did land, so a retry starts from a clean slate.
      if (uploaded.length) {
        await deleteAttachments(uploaded.map(({ assetPath }) => ({ storagePath: assetPath })));
      }
      return { uploaded: [], hasError: true };
    }

    return { uploaded, hasError: false };
  } catch (error) {
    Sentry.captureException(error, { extra: { coachId, storagePath } });
    return { uploaded: [], hasError: true };
  }
};

/**
 * The coach's live clients, as the broadcast modal reads them.
 *
 * Separated from the counting so one read can answer for many products: the roster
 * is the same whichever product is being counted, and a caller needing several
 * counts would otherwise fetch it once per count.
 *
 * Null on failure rather than an empty array: "this coach has no clients" and "we
 * could not find out" are different answers, and the confirmations quote the one
 * they get.
 *
 * @param {string} coachId - The coach whose clients to load.
 * @returns {Promise<Array<object>|null>} Live client documents, or null when unreadable.
 */
export const getCoachClients = async (coachId) => {
  try {
    return await Coach.getBroadcastMessageUsers(coachId);
  } catch (error) {
    Sentry.captureException(error, { extra: { coachId } });
    return null;
  }
};

/**
 * How many of those clients are live on a set of products.
 *
 * This is what the confirmations quote, so a coach knows the size of a send — and
 * of a removal — before committing to it.
 *
 * Counted distinctly: a client holding two of the products receives the document
 * once, and counting them twice would overstate the reach.
 *
 * @param {Array<object>} clients - From getCoachClients.
 * @param {Array<string>} productIds - The products to count across.
 * @returns {number} Live client count.
 */
export const countClientsOnProducts = (clients, productIds) => {
  if (!productIds.length) return 0;

  return clients.filter((client) => (
    client.activeProductIds.some((productId) => productIds.includes(productId))
  )).length;
};
